import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ── Mini bar chart component ───────────────────────────────────────
function BarChart({ data, metric }) {
  const max = Math.max(...data.map(d => d[metric]), 1);
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="flex items-end gap-[3px] h-28 w-full">
      {data.map((d, i) => {
        const pct = (d[metric] / max) * 100;
        const isToday = d.date === today;
        const label = d.date.slice(5); // MM-DD
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col items-center z-10 pointer-events-none">
              <div className="px-2 py-1 rounded-lg text-xs font-bold whitespace-nowrap"
                style={{ background:"#1a0a2e", border:"1px solid rgba(249,115,22,0.4)", color:"#fb923c" }}>
                {d.date}: {d[metric]}
              </div>
              <div className="w-2 h-2 rotate-45 -mt-1" style={{ background:"#1a0a2e", border:"1px solid rgba(249,115,22,0.4)", borderTop:"none", borderLeft:"none" }}/>
            </div>
            <div className="w-full rounded-t-sm transition-all duration-500 relative overflow-hidden"
              style={{
                height: `${Math.max(pct, 2)}%`,
                background: isToday
                  ? "linear-gradient(180deg,#f97316,#ec4899)"
                  : "rgba(249,115,22,0.25)",
                boxShadow: isToday ? "0 0 8px rgba(249,115,22,0.6)" : "none",
              }}>
              {isToday && (
                <div className="absolute inset-0 animate-pulse opacity-40"
                  style={{ background:"linear-gradient(180deg,#fff,transparent)" }}/>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Stat card ──────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon, delay = 0 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const target = parseInt(value) || 0;
    if (target === 0) { setCount(0); return; }
    let start = 0;
    const step = Math.ceil(target / 40);
    const iv = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(iv); }
      else setCount(start);
    }, 30);
    return () => clearInterval(iv);
  }, [value]);

  return (
    <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
      transition={{ delay, type:"spring", stiffness:200, damping:20 }}
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${color}25` }}>
      {/* Glow bg */}
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-20"
        style={{ background: color }}/>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-2xl">{icon}</span>
          <span className="text-xs font-semibold px-2 py-1 rounded-full"
            style={{ background:`${color}15`, color }}>{sub}</span>
        </div>
        <div className="text-3xl font-black mb-1" style={{ color, fontFamily:"'Space Grotesk',sans-serif" }}>
          {count.toLocaleString()}
        </div>
        <div className="text-xs font-medium" style={{ color:"#6b7280" }}>{label}</div>
      </div>
    </motion.div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function Dashboard() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState("visits"); // "visits" | "unique"
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await fetch("/api/visitors/stats");
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
        setLastRefresh(new Date());
        setError(null);
      } else {
        setError(data.message || "Failed to load");
      }
    } catch (e) {
      setError("Cannot reach server");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Auto refresh every 60 seconds
    const iv = setInterval(fetchStats, 60000);
    return () => clearInterval(iv);
  }, [fetchStats]);

  const today = new Date().toISOString().split("T")[0];
  const todayData = stats?.last30days?.find(d => d.date === today);
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  const yesterdayData = stats?.last30days?.find(d => d.date === yesterday);

  // Week total
  const last7 = stats?.last30days?.slice(-7) || [];
  const weekVisits = last7.reduce((s, d) => s + (d.visits || 0), 0);
  const weekUnique = last7.reduce((s, d) => s + (d.unique || 0), 0);

  return (
    <div className="min-h-screen text-white"
      style={{ background:"linear-gradient(135deg,#040208 0%,#0a0416 50%,#040208 100%)", fontFamily:"'Sora',sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=Space+Grotesk:wght@700;800&display=swap');
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-thumb { background:rgba(249,115,22,0.3); border-radius:2px; }
        @keyframes spin-slow { to { transform:rotate(360deg); } }
      `}</style>

      {/* Background orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-[0.06]"
          style={{ background:"radial-gradient(circle,#f97316,#ec4899)" }}/>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-[120px] opacity-[0.05]"
          style={{ background:"radial-gradient(circle,#8b5cf6,#3b82f6)" }}/>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <motion.div initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }}
          className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background:"linear-gradient(135deg,#f97316,#ec4899)" }}>
                📊
              </div>
              <h1 className="text-2xl font-black" style={{
                background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)",
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent"
              }}>
                Santharion Analytics
              </h1>
            </div>
            <p className="text-xs" style={{ color:"#4b5563" }}>
              learnaihub.in · Live dashboard
              {lastRefresh && ` · Updated ${lastRefresh.toLocaleTimeString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <motion.button onClick={fetchStats}
              whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition"
              style={{ background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.3)", color:"#fb923c" }}>
              {loading ? <span style={{ display:"inline-block", animation:"spin-slow 1s linear infinite" }}>⟳</span> : "⟳"} Refresh
            </motion.button>
            <a href="/ai-assistant"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition"
              style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", color:"#9ca3af" }}>
              ← Back to App
            </a>
          </div>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="mb-6 p-4 rounded-2xl text-sm font-medium"
              style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.3)", color:"#fca5a5" }}>
              ⚠️ {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading skeleton */}
        {loading && !stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="rounded-2xl p-5 animate-pulse h-28"
                style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}/>
            ))}
          </div>
        )}

        {stats && (
          <>
            {/* ── STAT CARDS ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label="Total Visits" value={stats.totalVisits}
                sub="All time" color="#f97316" icon="🌐" delay={0}/>
              <StatCard label="Unique Visitors" value={stats.uniqueVisitors}
                sub="All time" color="#ec4899" icon="👤" delay={0.05}/>
              <StatCard label="Today's Visits" value={stats.todayVisits}
                sub="Today" color="#8b5cf6" icon="📅" delay={0.1}/>
              <StatCard label="This Week" value={weekVisits}
                sub="7 days" color="#06b6d4" icon="📈" delay={0.15}/>
            </div>

            {/* ── TODAY HIGHLIGHT ── */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

              {/* Today details */}
              <div className="md:col-span-1 rounded-2xl p-5"
                style={{ background:"rgba(249,115,22,0.05)", border:"1px solid rgba(249,115,22,0.2)" }}>
                <h3 className="text-sm font-bold mb-4" style={{ color:"#fb923c" }}>📅 Today</h3>
                <div className="space-y-3">
                  {[
                    { label:"Visits",        value: stats.todayVisits    || 0, color:"#f97316" },
                    { label:"Unique",        value: stats.todayUnique    || 0, color:"#ec4899" },
                    { label:"Yesterday",     value: stats.yesterdayVisits|| 0, color:"#8b5cf6" },
                    { label:"Week total",    value: weekVisits,                 color:"#06b6d4" },
                    { label:"Week unique",   value: weekUnique,                 color:"#10b981" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-xs" style={{ color:"#6b7280" }}>{item.label}</span>
                      <span className="text-sm font-black" style={{ color: item.color }}>
                        {item.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3" style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                  <p className="text-xs" style={{ color:"#4b5563" }}>
                    First visit: {stats.firstVisit !== "N/A"
                      ? new Date(stats.firstVisit).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Chart */}
              <div className="md:col-span-2 rounded-2xl p-5"
                style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold" style={{ color:"#e5e7eb" }}>Last 30 Days</h3>
                  <div className="flex items-center gap-1 p-1 rounded-xl"
                    style={{ background:"rgba(255,255,255,0.04)" }}>
                    {["visits","unique"].map(t => (
                      <button key={t} onClick={() => setTab(t)}
                        className="px-3 py-1 rounded-lg text-xs font-semibold transition"
                        style={tab === t
                          ? { background:"linear-gradient(135deg,#f97316,#ec4899)", color:"#fff" }
                          : { color:"#6b7280" }}>
                        {t === "visits" ? "Visits" : "Unique"}
                      </button>
                    ))}
                  </div>
                </div>
                <BarChart data={stats.last30days || []} metric={tab}/>
                {/* X axis labels — show every 5th */}
                <div className="flex mt-1">
                  {(stats.last30days || []).map((d, i) => (
                    <div key={i} className="flex-1 text-center">
                      {i % 7 === 0 && (
                        <span className="text-[9px]" style={{ color:"#374151" }}>
                          {d.date.slice(5)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* ── DAILY TABLE ── */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.3 }}
              className="rounded-2xl overflow-hidden"
              style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)" }}>
              <div className="px-5 py-4" style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <h3 className="text-sm font-bold" style={{ color:"#e5e7eb" }}>📋 Daily Breakdown — Last 30 Days</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                      {["Date","Visits","Unique","% of Total"].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold"
                          style={{ color:"#4b5563" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...(stats.last30days || [])].reverse().map((d, i) => {
                      const isToday   = d.date === today;
                      const pct       = stats.totalVisits > 0
                        ? ((d.visits / stats.totalVisits) * 100).toFixed(1)
                        : "0.0";
                      return (
                        <tr key={i}
                          style={{
                            borderBottom:"1px solid rgba(255,255,255,0.03)",
                            background: isToday ? "rgba(249,115,22,0.05)" : "transparent"
                          }}>
                          <td className="px-5 py-3">
                            <span className="font-semibold" style={{ color: isToday ? "#fb923c" : "#e5e7eb" }}>
                              {d.date}
                            </span>
                            {isToday && (
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-bold"
                                style={{ background:"rgba(249,115,22,0.2)", color:"#fb923c" }}>Today</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <span className="font-bold" style={{ color: d.visits > 0 ? "#f97316" : "#374151" }}>
                              {d.visits.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="font-bold" style={{ color: d.unique > 0 ? "#ec4899" : "#374151" }}>
                              {d.unique.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full max-w-[80px]"
                                style={{ background:"rgba(255,255,255,0.06)" }}>
                                <div className="h-full rounded-full"
                                  style={{ width:`${pct}%`, background:"linear-gradient(90deg,#f97316,#ec4899)" }}/>
                              </div>
                              <span className="text-xs" style={{ color:"#6b7280" }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Footer */}
            <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.5 }}
              className="text-center text-xs mt-6" style={{ color:"#374151" }}>
              Auto-refreshes every 60 seconds · Santharion AI Analytics
            </motion.p>
          </>
        )}
      </div>
    </div>
  );
}
